import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LoaderBalloonScreen } from '@crazyegginc/hatch';

import { useAuthContext, useMutation } from '/src/hooks';
import { createStripeCustomerSessionMutation } from '../mutations';
import { stripeConfig } from '../config';
import { PLAN_TYPES, SUBSCRIPTION_STATES, MANAGE_PAYMENT_EXTERNAL } from '/src/features/_global/constants';

function useStripePricingTableScript() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (document.querySelector('script[src="https://js.stripe.com/v3/pricing-table.js"]')) {
      setLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/pricing-table.js';
    script.async = true;
    script.onload = () => setLoaded(true);
    document.head.appendChild(script);
  }, []);

  return loaded;
}

export function SubscribePage() {
  const navigate = useNavigate();
  const { currentUser, currentAccount, subscription } = useAuthContext();
  const scriptLoaded = useStripePricingTableScript();
  const [clientSecret, setClientSecret] = useState(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);

  const isFree = subscription?.plan?.type === PLAN_TYPES.FREE;
  const isCanceled = subscription?.state === SUBSCRIPTION_STATES.CANCELED;
  const isTrial = subscription?.plan?.type === PLAN_TYPES.TRIAL;
  const shouldShowPricingTable = isFree || isCanceled || isTrial;
  const isStripeUser = Boolean(currentAccount?.useStripe);
  const isShopifyUser = Boolean(currentAccount?.isShopifyUser);

  const mutation = useMutation(createStripeCustomerSessionMutation);

  useEffect(() => {
    if (isShopifyUser) {
      navigate('/subscriptions/new/bundled/plans/monthly', { replace: true });
      return;
    }

    if (!shouldShowPricingTable) {
      if (isStripeUser) {
        navigate(MANAGE_PAYMENT_EXTERNAL, { replace: true });
      } else {
        navigate('/subscriptions/new/bundled/plans/monthly', { replace: true });
      }
    }
  }, [isShopifyUser, shouldShowPricingTable, isStripeUser, navigate]);

  useEffect(() => {
    mutation.mutate(undefined, {
      onSuccess: (data) => {
        setClientSecret(data?.createStripeCustomerSession?.clientSecret || null);
        setSessionLoaded(true);
      },
      onError: () => {
        setSessionLoaded(true);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isShopifyUser || !shouldShowPricingTable) {
    return <LoaderBalloonScreen text="Redirecting..." />;
  }

  if (!scriptLoaded || !sessionLoaded) {
    return <LoaderBalloonScreen text="Loading subscription options..." />;
  }

  return (
    <div className="px-10 w-full">
      <div className="mt-4">
        <Link to="/account/billing" className="text-link hover:underline">
          ← Back to Billing
        </Link>
      </div>
      <div className="flex h-[85px] items-center">
        <h1 className="text-header-1 my-4 flex-grow truncate leading-none" title="Choose Plan">
          Choose Plan
        </h1>
      </div>
      {clientSecret ? (
        <stripe-pricing-table
          pricing-table-id={stripeConfig.pricingTableId}
          publishable-key={stripeConfig.publishableKey}
          customer-session-client-secret={clientSecret}
          client-reference-id={String(currentUser?.userId)}
        />
      ) : (
        <stripe-pricing-table
          pricing-table-id={stripeConfig.pricingTableId}
          publishable-key={stripeConfig.publishableKey}
          customer-email={currentUser?.email}
          client-reference-id={String(currentUser?.userId)}
        />
      )}
    </div>
  );
}
